// The on-map hero: composes the paper-doll appearance into per-animation frame
// textures and runs a small state machine (idle / walk / action / dying) with
// 4-way facing (side mirrored for left/right). Named actions overlay the matching
// instrument sheet (axe, hoe, watering can, …). Movement is click-to-walk + WASD.
import { Sprite, Texture, Container, Rectangle, Assets } from 'pixi.js';
import { composeCharacter, type ToolOverlay } from '../character/compositor';
import type { Appearance } from '../character/compositor';
import { FRAME_W, FRAME_H } from '../character/manifest.generated';
import { TILE } from './world';

export type Dir = 'down' | 'up' | 'left' | 'right';
type Facing = 'down' | 'up' | 'side';
type State = 'idle' | 'walk' | 'action' | 'dying';
export type ActionType = 'chop' | 'till' | 'mine' | 'water' | 'fish' | 'attack' | 'bow';

// ─── Animation layout on the Cute Fantasy Player sheet (64px frames, owner-mapped)
// The sheet is triplets of (down, side, up). Movement rows and per-action row
// blocks; each action block overlays a tool sheet whose own rows align 1:1.
const MOVE: Record<'idle' | 'walk', Record<Facing, number>> = {
  idle: { down: 0, side: 1, up: 2 },
  walk: { down: 3, side: 4, up: 5 },
};
const FACE_IDX: Record<Facing, number> = { down: 0, side: 1, up: 2 };
/**
 * Sitting in the saddle. The sheet HAS these poses and nothing used them: mounting
 * simply replayed the walk rows faster, so the hero jogged along beside/through his
 * horse instead of riding it.
 *
 * Rows 50/51/52 = seated idle (2 frames, down/side/up), 53/54/55 = riding (6
 * frames). The art is the RIDER ONLY — legs splayed, hands forward on the reins,
 * no ground contact — so on its own it reads as a small green torso floating in
 * the air, which is exactly what it looked like.
 *
 * The mount comes from Player_Horse_Brown.png, composited UNDER the rider by the
 * same tool-overlay mechanism the tools use (composeBase already reserves the
 * `behind` slot for it: "horse / up-facing tool sits under the hero"). That sheet
 * is a 6x6 grid of 64px frames whose rows mirror these one-for-one — horse row 0
 * is rider row 50, and both idle blocks are 2 frames while both walk blocks are 6.
 * Composing them as ONE frame is what makes horse and rider line up exactly: they
 * share a transform, a scale and an anchor, so no amount of drift is possible.
 */
const HORSE_SHEET = 'tools/Player_Horse_Brown.png';
/** Rider row → its row on the horse sheet. */
const horseRowFor = (riderRow: number) => riderRow - RIDE.idle.down;
const RIDE: Record<'idle' | 'walk', Record<Facing, number>> = {
  idle: { down: 50, side: 51, up: 52 },
  walk: { down: 53, side: 54, up: 55 },
};
// base = player down-row of the block; toolBase = the tool sheet's down-row.
const ACTIONS: Record<ActionType, { base: number; toolFile: string; toolBase: number }> = {
  // Sword attack down/side/up = rows 8/9/10 (front/profile/BACK). The old base:6 row
  // block (6/7/8) is all FRONT-facing, so "attack up" rendered facing south — that's
  // the "hero watches the wrong way" bug. Iron_Sword.png is the 9-row attack subset
  // (body row 6 = sword row 0), so body row 8 = sword row 2 → toolBase 2 keeps aligned.
  attack: { base: 8,  toolFile: 'tools/Iron_Sword.png',          toolBase: 2 },
  bow:    { base: 29, toolFile: 'tools/Wooden_Bow.png',          toolBase: 0 },
  chop:   { base: 32, toolFile: 'tools/Iron_Tools.png',          toolBase: 0 },  // axe
  // SWAPPED 2026-08-16, verified by the owner against player-animations.html:
  // rows 35/36/37 are the PICKAXE motion and 38/39/40 are the HOE motion, and this
  // table had them the other way round. So every mining swing played the tilling
  // body with a pickaxe drawn into it, and every till played the mining body with a
  // hoe — which is the "we are using incorrect animation for the item usage" that
  // survived three earlier attempts, because each one moved the TOOL overlay to
  // match the body instead of asking whether the body was right.
  //
  // The tool rows are unchanged: Iron_Tools is [0-2 axe, 3-5 hoe, 6-8 pickaxe,
  // 9-11 watering can], so the hoe still rides the hoe motion and the pickaxe the
  // pickaxe motion — they simply travel with the other body block now.
  mine:   { base: 35, toolFile: 'tools/Iron_Tools.png',          toolBase: 6 },  // pickaxe
  till:   { base: 38, toolFile: 'tools/Iron_Tools.png',          toolBase: 3 },  // hoe
  water:  { base: 41, toolFile: 'tools/Iron_Tools.png',          toolBase: 9 },  // watering can
  fish:   { base: 44, toolFile: 'tools/Wooden_Fishing_Rod.png',  toolBase: 0 },
};
// Held-item layer (§1). A tool/weapon the hero carries in hand during idle/walk/
// roll — a separate draw layer (front-hand, or back-hand when facing up). The tool
// sheets are authored to composite directly onto the 64px character frame, so the
// hand position is baked into the art: the held sprite is just the tool's rest
// frame (column 0 of the matching action row) drawn at the SAME transform as the
// body. During a baked tool ACTION (chop/attack/…) the held layer hides — those
// frames already contain the swinging tool. `base` = the tool sheet's down-row.
// Per-tool `grip` (native px) pulls the carried item into the hand. The rest frame
// is column 0 of the tool's SWING row — a raised wind-up pose — so an axe/hoe/pick
// sits high and out to the side unless nudged down toward the hand. The sword sits
// right already. dx flips with facing (it's a child of the flipped root).
const HELD: Record<string, { file: string; base: number; grip: { dx: number; dy: number } }> = {
  sword:   { file: 'tools/Iron_Sword.png', base: 0, grip: { dx: 0, dy: 0 } },
  axe:     { file: 'tools/Iron_Tools.png', base: 0, grip: { dx: -4, dy: 13 } },
  hoe:     { file: 'tools/Iron_Tools.png', base: 3, grip: { dx: 0, dy: 13 } },
  pickaxe: { file: 'tools/Iron_Tools.png', base: 6, grip: { dx: 4, dy: 13 } },
};
// §1 hand-anchor table. The carried item is a single rest frame (col 0 of the
// action block), so it does not bob on its own the way the body art does across a
// walk cycle — left rigid, it visibly "lags a frame behind" the hand. These are a
// per-facing nudge (native px, dx flips with the sprite) plus a per-walk-frame
// vertical bob so the grip stays glued to the hand. Tuned visually in-game; keep
// dx/dy small — the tool sheets are authored frame-aligned to the body already.
const HELD_ANCHOR: Record<Facing, { dx: number; dy: number }> = {
  down: { dx: 0, dy: 0 },
  side: { dx: 1, dy: 0 },
  up:   { dx: 0, dy: 0 },
};
// Native-px lift per walk-frame index — mirrors the body's 3-frame walk bob so the
// held item rises and falls with the hand instead of floating.
const WALK_BOB = [0, -1, 0] as const;
const DYING_ROW = 15;              // single-direction fall→collapse
// Jump (Space) / roll (Z) are ROW blocks in the shared 56-row layout, composed
// from ALL paper-doll layers (like the walk) so the hero keeps his outfit and
// correct size. Each block is 3 rows: down / side / up. Adjust `base` if the
// in-game animation is wrong (find the right row in the sprite viewer).
const MOVES = {
  jump: { base: 26, fps: 11, dash: 0 },          // rows 26/27/28 (down/side/up), 6 frames
  roll: { base: 17, fps: 16, dash: 2.2 * 48 },   // rows 17/18/19, 8 frames · TILE = 48
} as const;
export type MoveType = keyof typeof MOVES;
const SIDE_FACES_RIGHT = true;     // side art faces right → left is the flipped one (verified in-game)
const FPS = { idle: 5, walk: 9, action: 12, dying: 8 } as const;
const MAX_FRAMES = 9;

/** Tiles per second by gait. Mounted is set when riding (Block 1: horses). */
export const GAIT = { walk: 4, run: 6.5, mount: 8 } as const;
export type Gait = keyof typeof GAIT;
// A ridden horse overrides the mount speed with its type's (work 7 / racing 10 /
// heavy 6). setMountSpeed() sets it; setGait('mount') then uses it.

/**
 * How long a direction press is honoured after it lands.
 *
 * Without this, a key tapped a few frames before the hero finishes an action (or
 * between two frames of a stall) is simply lost, and the hero ignores an input
 * the player definitely made. Holding it for 120ms means a press always lands
 * somewhere, which is the difference between "responsive" and "fighting it".
 */
const INPUT_BUFFER_MS = 120;
const CHAR_ANCHOR_Y = 0.9;
// Exported so the loose-horse sprite (riding.ts) can be checked against the size the
// ridden horse is drawn at — the mount is composited into this same hero frame.
export const CHAR_SCALE = (3.2 * TILE) / FRAME_H;
// The composed hero's feet sit at ~0.64 down the 64px frame while the sprite is
// anchored at 0.9, so the feet render ~this many world-px ABOVE the sprite's
// position point. Click-to-walk adds this to the target Y so the hero's FEET
// land on the clicked spot (not a point above it).
const FOOT_FRAC = 0.64;
export const FOOT_OFFSET = (CHAR_ANCHOR_Y - FOOT_FRAC) * FRAME_H * CHAR_SCALE;
// Death → respawn timings (seconds).
const DEATH_HOLD = 1.8;            // lie on the ground before vanishing
const RESPAWN_BLINK = 1.8;         // idle flicker after reappearing

const facingOf = (d: Dir): Facing => (d === 'down' ? 'down' : d === 'up' ? 'up' : 'side');
const flipOf = (d: Dir): boolean => (d === 'left' ? SIDE_FACES_RIGHT : d === 'right' ? !SIDE_FACES_RIGHT : false);

export interface PlayerHandle {
  sprite: Container;   // root: [back-hand · body · front-hand]
  x: number;
  y: number;
  moving: boolean;
  dir: Dir;
  setTarget(wx: number, wy: number): void;
  /** Follow a sequence of world-point waypoints (from the pathfinder). */
  setPath(points: { x: number; y: number }[]): void;
  setKeys(vx: number, vy: number): void;
  /** Show a held item in the hero's hand (§1): 'sword' | 'axe' | 'hoe' | 'pickaxe',
   *  or null for an empty hand. Rendered on the front/back-hand layer. */
  setHeld(item: string | null): void;
  /** Tint the held tool/weapon toward its crafted tier's metal (0xffffff = none).
   *  The pack has ONE sprite per tool and none per tier, so colour is what tells a
   *  Mythril sword from a wooden one in the hand — the owner's explicit call. */
  setHeldTint(tint: number): void;
  /** Which gait the hero moves at. Running also burns energy (farm.ts meters it). */
  setGait(g: Gait): void;
  gait: Gait;
  /** Seat the hero on (or take him off) the horse — swaps to the saddle rows. */
  setRiding(on: boolean): void;
  /** Ridden speed (tiles/sec) used while gait is 'mount'. */
  setMountSpeed(tilesPerSec: number): void;
  /** One-shot instrument swing facing `dir` (e.g. hoe till); resolves when it ends. */
  playAction(type: ActionType, dir?: Dir): Promise<void>;
  /** Looping instrument action facing `dir` (e.g. chopping while held); stopAction to end. */
  startAction(type: ActionType, dir: Dir): void;
  stopAction(): void;
  /** Cancel a one-shot action (e.g. an arena swing) so movement can resume instantly. */
  interruptAction(): void;
  /** Hard-set the world position (e.g. keep the player inside the arena safe zone). */
  setPos(x: number, y: number): void;
  /** One-shot movement (jump on Space / roll on Z); roll dashes forward. */
  playMove(type: MoveType, dir?: Dir): Promise<void>;
  /** Play the death anim, hold on the ground, vanish, then respawn with a blink. */
  playDeath(): Promise<void>;
  update(
    dtSec: number,
    clampPos: (x: number, y: number) => [number, number],
    blocked: (wx: number, wy: number) => boolean,
  ): void;
  destroy(): void;
}

/** True if a composed frame has any visible pixel (used to find a row's length). */
function hasPixels(canvas: HTMLCanvasElement): boolean {
  const c = canvas.getContext('2d');
  if (!c) return true;
  const d = c.getImageData(0, 0, canvas.width, canvas.height).data;
  for (let i = 3; i < d.length; i += 4) if (d[i] > 8) return true;
  return false;
}

/** Compose one sheet row's frames (optionally with a tool overlay), auto-detecting length. */
async function buildRow(app: Appearance, row: number, tool?: ToolOverlay): Promise<Texture[]> {
  const frames: Texture[] = [];
  for (let f = 0; f < MAX_FRAMES; f++) {
    const canvas = await composeCharacter(app, { fx: f, fy: row, scale: 1, tool });
    if (f > 0 && !hasPixels(canvas)) break;
    frames.push(Texture.from(canvas));
  }
  return frames.length ? frames : [Texture.from(await composeCharacter(app, { fx: 0, fy: row, scale: 1 }))];
}

export async function createPlayer(app: Appearance, startX: number, startY: number): Promise<PlayerHandle> {
  // Movement rows composed once, cached by row number.
  const moveCache = new Map<number, Texture[]>();
  for (const st of Object.values(MOVE)) for (const row of Object.values(st))
    if (!moveCache.has(row)) moveCache.set(row, await buildRow(app, row));
  // Saddle rows carry the horse as a behind-overlay, so each cached frame is the
  // finished horse+rider composite. Composed ON FIRST MOUNT rather than up front:
  // six extra rows is real work at every player creation, and most sessions never
  // tame a horse. Until they are ready the hero keeps his on-foot frames, so the
  // worst case is a few frames of walking animation instead of a missing sprite.
  // Declared HERE, with the rest of the ride state, because moveFrames() below
  // closes over it and createPlayer calls that during construction (the priming
  // h.update at the end). Declaring it further down put it in the temporal dead
  // zone: "Cannot access 'riding' before initialization", and no hero at all.
  let riding = false;                    // true while seated: use the saddle rows
  let rideReady = false;
  let rideBuilding: Promise<void> | null = null;
  const ensureRideRows = () => {
    if (rideReady || rideBuilding) return rideBuilding;
    rideBuilding = (async () => {
      for (const st of Object.values(RIDE)) for (const row of Object.values(st))
        if (!moveCache.has(row))
          moveCache.set(row, await buildRow(app, row, { file: HORSE_SHEET, row: horseRowFor(row), behind: true }));
      rideReady = true;
    })();
    return rideBuilding;
  };
  // Riding swaps the whole movement row block; everything downstream (facing,
  // flipping, frame count) is row-driven, so nothing else has to know.
  const moveFrames = (state: 'idle' | 'walk', dir: Dir) =>
    moveCache.get((riding && rideReady ? RIDE : MOVE)[state][facingOf(dir)])!;

  // Action rows are composed lazily (with their tool overlay) the first time used.
  const moveSheetCache = new Map<string, Texture[]>();   // jump/roll frames, keyed `type:facing`
  const actionCache = new Map<string, Texture[]>();
  const actionFrames = async (type: ActionType, dir: Dir): Promise<Texture[]> => {
    const facing = facingOf(dir);
    const key = `${type}:${facing}`;
    let frames = actionCache.get(key);
    if (!frames) {
      const cfg = ACTIONS[type];
      const idx = FACE_IDX[facing];
      const tool: ToolOverlay = { file: cfg.toolFile, row: cfg.toolBase + idx, behind: facing === 'up' };
      frames = await buildRow(app, cfg.base + idx, tool);
      actionCache.set(key, frames);
    }
    return frames;
  };
  let dyingFrames: Texture[] | null = null;

  // Body + held-item layers in a small container (§1): the held item draws BEHIND
  // the hero when facing up (back-hand) or in FRONT otherwise, and flips with him.
  // Public `sprite` is the container; the body sprite inside carries the animation.
  const body = new Sprite(moveFrames('idle', 'down')[0]);
  body.anchor.set(0.5, CHAR_ANCHOR_Y);
  body.scale.set(CHAR_SCALE);
  const heldBack = new Sprite(Texture.EMPTY);
  const heldFront = new Sprite(Texture.EMPTY);
  for (const s of [heldBack, heldFront]) { s.anchor.set(0.5, CHAR_ANCHOR_Y); s.scale.set(CHAR_SCALE); s.visible = false; }
  const root = new Container();
  // The hero is drawn over whatever he stands in front of; he must not intercept
  // taps meant for it (see the eventMode note in views/farm/plots.ts).
  root.eventMode = 'none';
  root.addChild(heldBack, body, heldFront);   // back-hand · body · front-hand

  // Held-item rest frames, sliced lazily from a tool sheet (col 0 of the action row).
  let heldItem: string | null = null;
  const heldCache = new Map<string, Texture>();
  const heldSheet = new Map<string, Texture>();
  const loadHeldSheet = async (file: string) => {
    if (heldSheet.has(file)) return;
    try { heldSheet.set(file, await Assets.load(`/player/${file}`)); } catch { /* missing → empty hand */ }
  };
  const heldFrame = (item: string, facing: Facing): Texture | null => {
    const cfg = HELD[item]; if (!cfg) return null;
    const key = `${item}:${facing}`;
    let t = heldCache.get(key);
    if (!t) {
      const sheet = heldSheet.get(cfg.file);
      if (!sheet) { void loadHeldSheet(cfg.file); return null; }   // still loading
      const row = cfg.base + FACE_IDX[facing];
      t = new Texture({ source: sheet.source, frame: new Rectangle(0, row * FRAME_H, FRAME_W, FRAME_H) });
      heldCache.set(key, t);
    }
    return t;
  };
  // Show/hide + pick the held frame for the current facing. `show` is false during
  // baked tool actions + death (those frames already contain the tool, or none).
  const updateHeld = (facing: Facing, show: boolean, bobIdx = 0) => {
    if (!heldItem || !show) { heldBack.visible = heldFront.visible = false; return; }
    const t = heldFrame(heldItem, facing);
    if (!t) { heldBack.visible = heldFront.visible = false; return; }
    const back = facing === 'up';
    const s = back ? heldBack : heldFront;
    s.texture = t;
    // Anchor the grip to the hand: per-tool grip + per-facing nudge + walk-cycle bob.
    const a = HELD_ANCHOR[facing];
    const g = HELD[heldItem].grip;
    const bob = WALK_BOB[bobIdx % WALK_BOB.length];
    s.position.set((a.dx + g.dx) * CHAR_SCALE, (a.dy + g.dy + bob) * CHAR_SCALE);
    heldBack.visible = back; heldFront.visible = !back;
  };

  let target: { x: number; y: number } | null = null;
  let path: { x: number; y: number }[] = [];        // remaining waypoints after `target`
  let keyVx = 0, keyVy = 0;
  // Buffered direction: a press is honoured for INPUT_BUFFER_MS after it lands,
  // so an input made during an action isn't silently dropped on the floor.
  let bufVx = 0, bufVy = 0, bufLeft = 0;
  let gait: Gait = 'walk';
  let mountSpeed: number = GAIT.mount;   // overridden by the tamed horse's type
  let animT = 0, frameIdx = 0;
  let state: State = 'idle';
  // Active action (chop/hoe/…) or movement one-shot (jump/roll). loop = held;
  // one-shot resolves its promise. fps/dash override the defaults (jump/roll).
  let action:
    | { type: string; frames: Texture[]; dir: Dir; loop: boolean; resolve?: () => void; fps?: number; dashLeft?: number; dashSpeed?: number }
    | null = null;
  // Death sub-state machine (die → hold → gone → respawn-blink).
  let death: { phase: 'die' | 'hold' | 'gone' | 'blink'; t: number; resolve?: () => void } | null = null;

  // Sort by the FEET, not the sprite anchor.
  //
  // Every other entity in the scene keys its zIndex off where it touches the
  // ground: decor uses `(ty + 1) * TILE`, buildings the same, animals their foot
  // position. The hero was the exception — his anchor sits FOOT_OFFSET BELOW his
  // feet, so his sort key was always that much larger than an object standing on
  // the same row, and he drew in front of things he was standing behind.
  const place = () => { root.x = h.x; root.y = h.y; root.zIndex = h.y - FOOT_OFFSET; };
  const setFlip = (dir: Dir) => { root.scale.x = flipOf(dir) ? -1 : 1; };
  const drawMove = (st: 'idle' | 'walk', dir: Dir, idx: number) => {
    const frames = moveFrames(st, dir);
    body.texture = frames[idx % frames.length];
    setFlip(dir);
    place();
  };

  const h: PlayerHandle = {
    sprite: root,
    x: startX,
    y: startY,
    moving: false,
    dir: 'down',
    setTarget(wx, wy) { if (action?.loop || death) return; target = { x: wx, y: wy }; path = []; keyVx = keyVy = 0; },
    setPath(points) {
      if (action?.loop || death) return;
      keyVx = keyVy = 0;
      if (!points.length) { target = null; path = []; return; }
      target = points[0]; path = points.slice(1);
    },
    setHeld(item) { heldItem = item && HELD[item] ? item : null; if (heldItem) void loadHeldSheet(HELD[heldItem].file); },
    setHeldTint(tint) { heldBack.tint = heldFront.tint = tint; },
    get gait() { return gait; },
    setGait(g) { gait = g; },
    setMountSpeed(tps) { mountSpeed = tps; },
    setRiding(on) {
      if (on) void ensureRideRows();   // compose the saddle rows the first time they are needed
      if (riding === on) return;
      riding = on;
      frameIdx = 0;   // the seated block has a different frame count than the walk
    },
    setKeys(vx, vy) {
      if (death) return;
      if (action?.loop) {
        // Mid-swing. Remember the press briefly instead of dropping it: the hero
        // should start moving the instant the action ends, not need a re-press.
        if (vx || vy) { bufVx = vx; bufVy = vy; bufLeft = INPUT_BUFFER_MS / 1000; }
        return;
      }
      keyVx = vx; keyVy = vy;
      if (vx || vy) { target = null; path = []; }
    },
    async playAction(type, dir) {
      if (death) return;
      if (dir) h.dir = dir;
      keyVx = keyVy = 0; target = null;
      const frames = await actionFrames(type, h.dir);
      state = 'action'; frameIdx = 0; animT = 0;
      return new Promise<void>((resolve) => { action = { type, frames, dir: h.dir, loop: false, resolve }; });
    },
    startAction(type, dir) {
      if (death) return;
      h.dir = dir; keyVx = keyVy = 0; target = null;
      state = 'action'; frameIdx = 0; animT = 0;
      // Compose asynchronously; until ready the sprite just holds its current frame.
      // …and only adopt them if the SAME action is still running in the SAME
      // direction — otherwise a quick re-target (chop east, then chop north)
      // stamped the first row's frames onto the new facing and the hero swung
      // sideways at a tree in front of him.
      void actionFrames(type, dir).then((frames) => {
        if (action && action.type === type && action.dir === dir && action.loop) action.frames = frames;
      });
      action = { type, frames: moveFrames('idle', dir), dir, loop: true };
    },
    stopAction() { if (action?.loop) { action = null; state = 'idle'; frameIdx = 0; } },
    interruptAction() {
      // Drop a one-shot swing (arena auto-attack) immediately — the hit already
      // resolved server-/logic-side, the frames are just cosmetic — so held keys /
      // a fresh click can move the hero this very frame instead of after the swing.
      if (action && !action.loop) { const done = action.resolve; action = null; state = 'idle'; frameIdx = 0; done?.(); }
    },
    setPos(x, y) { h.x = x; h.y = y; place(); },
    async playMove(type, dir) {
      if (death || action) return;
      // Nobody rolls or jumps out of a saddle. MOVES rows are the ON-FOOT sheets,
      // so playing one while seated dropped the horse from under the hero for the
      // length of the animation (and a roll dashed him off it). The caller refuses
      // the key too (views/farm/input.ts oneShotMoveRefusal) and says so; this is
      // the backstop for every other caller.
      if (riding) return;
      if (dir) h.dir = dir;
      keyVx = keyVy = 0; target = null; path = [];
      const cfg = MOVES[type];
      const facing = facingOf(h.dir);
      const key = `${type}:${facing}`;
      let frames = moveSheetCache.get(key);
      if (!frames) { frames = await buildRow(app, cfg.base + FACE_IDX[facing]); moveSheetCache.set(key, frames); }
      state = 'action'; frameIdx = 0; animT = 0;
      const dur = frames.length / cfg.fps;
      return new Promise<void>((resolve) => {
        action = { type, frames, dir: h.dir, loop: false, resolve, fps: cfg.fps,
                   dashLeft: cfg.dash, dashSpeed: cfg.dash > 0 ? cfg.dash / dur : 0 };
      });
    },
    async playDeath() {
      if (death) return;
      if (!dyingFrames) dyingFrames = await buildRow(app, DYING_ROW);
      action = null; target = null; keyVx = keyVy = 0;
      state = 'dying'; frameIdx = 0; animT = 0;
      return new Promise<void>((resolve) => { death = { phase: 'die', t: 0, resolve }; });
    },
    update(dt, clampPos, blocked) {
      // ── Death / respawn cycle ────────────────────────────────────────────
      if (death) {
        h.moving = false;
        const frames = dyingFrames!;
        death.t += dt;
        if (death.phase === 'die') {
          animT += dt;
          if (animT >= 1 / FPS.dying) { animT = 0; if (frameIdx < frames.length - 1) frameIdx++; else { death.phase = 'hold'; death.t = 0; } }
          body.texture = frames[frameIdx]; body.alpha = 1;
        } else if (death.phase === 'hold') {
          body.texture = frames[frames.length - 1]; body.alpha = 1;
          if (death.t >= DEATH_HOLD) { death.phase = 'gone'; death.t = 0; }
        } else if (death.phase === 'gone') {
          body.alpha = Math.max(0, 1 - death.t / 0.4);
          if (death.t >= 0.5) { death.phase = 'blink'; death.t = 0; frameIdx = 0; }
        } else { // blink: reappear at idle-down, flicker for RESPAWN_BLINK
          animT += dt;
          if (animT >= 1 / FPS.idle) { animT = 0; frameIdx = (frameIdx + 1) % moveFrames('idle', 'down').length; }
          drawMove('idle', 'down', frameIdx);
          body.alpha = Math.floor(death.t * 9) % 2 ? 0.25 : 1;
          if (death.t >= RESPAWN_BLINK) { const done = death.resolve; death = null; body.alpha = 1; state = 'idle'; frameIdx = 0; h.dir = 'down'; done?.(); }
        }
        if (death) { root.scale.x = 1; heldBack.visible = heldFront.visible = false; place(); }
        return;
      }

      // ── Instrument action / movement one-shot (jump, roll) ───────────────
      if (action) {
        // While a roll is dashing, report "moving" so the camera follows him.
        h.moving = !!(action.dashLeft && action.dashLeft > 0);
        // Forward dash (roll): glide in the facing direction. §2.4: collision stays
        // ON during the roll (i-frames are damage-only, elsewhere), checked in
        // FEET space like walking — not the sprite origin — and SWEPT in small
        // sub-steps so the roll stops clamped at the last valid position instead of
        // tunnelling or snagging inconsistently on a trunk.
        if (action.dashLeft && action.dashLeft > 0) {
          const step = Math.min(action.dashLeft, (action.dashSpeed ?? 0) * dt);
          action.dashLeft -= step;
          const dv = action.dir === 'left' ? [-1, 0] : action.dir === 'right' ? [1, 0] : action.dir === 'up' ? [0, -1] : [0, 1];
          const solidAt = (x: number, y: number) => blocked(x, y - FOOT_OFFSET);
          const subs = Math.max(1, Math.ceil(step / 4));   // ≤4px per swept sub-step
          const sx = (dv[0] * step) / subs, sy = (dv[1] * step) / subs;
          for (let i = 0; i < subs; i++) {
            const nx = h.x + sx, ny = h.y + sy;
            if (solidAt(nx, ny)) { action.dashLeft = 0; break; }   // wall → clamp here, end the dash
            const [cx, cy] = clampPos(nx, ny); h.x = cx; h.y = cy;
          }
        }
        animT += dt;
        if (animT >= 1 / (action.fps ?? FPS.action)) {
          animT = 0; frameIdx++;
          if (!action.loop && frameIdx >= action.frames.length) {
            const done = action.resolve; action = null; state = 'idle'; frameIdx = 0;
            drawMove('idle', h.dir, 0);
            done?.();
            return;
          }
        }
        body.texture = action.frames[frameIdx % action.frames.length];
        setFlip(action.dir);
        // Held item hides during a baked tool action (the frame contains the tool);
        // a jump/roll keeps it in hand.
        updateHeld(facingOf(action.dir), false);   // never show the (mis-posed) carried tool
        place();
        return;
      }

      // ── Movement ─────────────────────────────────────────────────────────
      // A press made during an action replays the moment the action releases.
      if (bufLeft > 0) {
        bufLeft -= dt;
        if (!action && (bufVx || bufVy)) {
          keyVx = bufVx; keyVy = bufVy; target = null; path = [];
          bufVx = bufVy = 0; bufLeft = 0;
        } else if (bufLeft <= 0) {
          bufVx = bufVy = 0;
        }
      }

      const speed = (gait === 'mount' ? mountSpeed : GAIT[gait]) * TILE;      // px/sec
      let dx = 0, dy = 0;
      if (keyVx || keyVy) {
        const m = Math.hypot(keyVx, keyVy) || 1;
        dx = (keyVx / m) * speed * dt; dy = (keyVy / m) * speed * dt;
      } else if (target) {
        const tdx = target.x - h.x, tdy = target.y - h.y;
        const dist = Math.hypot(tdx, tdy);
        if (dist <= speed * dt + 1) { dx = tdx; dy = tdy; target = path.length ? path.shift()! : null; }
        else { dx = (tdx / dist) * speed * dt; dy = (tdy / dist) * speed * dt; }
      }
      // Collision at the hero's FEET (which sit ~FOOT_OFFSET above the sprite's
      // position point) so he stops at a trunk/rock instead of overlapping it.
      const solidAt = (x: number, y: number) => blocked(x, y - FOOT_OFFSET);
      let nx = h.x + dx, ny = h.y + dy;
      if (solidAt(nx, ny)) {
        if (!solidAt(nx, h.y)) ny = h.y;
        else if (!solidAt(h.x, ny)) nx = h.x;
        else { nx = h.x; ny = h.y; target = null; path = []; }
      }
      const [cx, cy] = clampPos(nx, ny);
      const mvx = cx - h.x, mvy = cy - h.y;
      h.moving = Math.abs(mvx) > 0.01 || Math.abs(mvy) > 0.01;
      h.x = cx; h.y = cy;

      const next: 'idle' | 'walk' = h.moving ? 'walk' : 'idle';
      if (h.moving) {
        // Facing hysteresis: on a ~45° diagonal mvx≈mvy, and picking by the bigger
        // axis each frame flickered between side and up/down ("front and side, front
        // and side"). Only switch axis when one clearly dominates; otherwise keep the
        // current axis and just update its sign.
        const ax = Math.abs(mvx), ay = Math.abs(mvy);
        const horiz = h.dir === 'left' || h.dir === 'right';
        if (ax > ay * 1.3) h.dir = mvx < 0 ? 'left' : 'right';
        else if (ay > ax * 1.3) h.dir = mvy < 0 ? 'up' : 'down';
        else if (horiz) h.dir = mvx < 0 ? 'left' : 'right';
        else h.dir = mvy < 0 ? 'up' : 'down';
      }
      if (next !== state) { state = next; frameIdx = 0; animT = 0; }
      animT += dt;
      const fps = next === 'walk' ? FPS.walk : FPS.idle;
      if (animT >= 1 / fps) { animT = 0; frameIdx = (frameIdx + 1) % moveFrames(next, h.dir).length; }
      body.alpha = 1;
      drawMove(next, h.dir, frameIdx);
      // No carried tool/weapon during idle/walk — the tool sheets only have swing
      // poses, so a "carried" frame always floated wrong. The tool appears only during
      // its baked action swing (chop/mine/till/attack), which is aligned to the body.
      updateHeld(facingOf(h.dir), false);
    },
    destroy() {
      for (const frames of moveCache.values()) frames.forEach((t) => t.destroy(true));
      for (const frames of moveSheetCache.values()) frames.forEach((t) => t.destroy(true));
      for (const frames of actionCache.values()) frames.forEach((t) => t.destroy(true));
      dyingFrames?.forEach((t) => t.destroy(true));
      heldCache.forEach((t) => t.destroy());
      root.destroy({ children: true });
    },
  };
  h.update(0, (x, y) => [x, y], () => false);
  return h;
}
